import {Kbd, TextInput, createStyles} from '@mantine/core';
import React from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const shortcutKeys = isMac ? ['⌘', '/'] : ['Ctrl', '/'];

const CONTAINER_HEIGHT = 49;
const INPUT_HEIGHT = 29;
const PILL_EDGE_PADDING = 8;
const PILL_GAP = 8;
const PILL_RADIUS = 30;
const ICON_SIZE = 14;
const HINT_CHIP_WIDTH = 35;
const ICON_WIDTH = PILL_EDGE_PADDING + ICON_SIZE + PILL_GAP;
const HINT_WIDTH = PILL_GAP + HINT_CHIP_WIDTH + PILL_EDGE_PADDING;

const useStyles = createStyles((theme, {showHint}: {showHint: boolean}) => ({
  root: {
    width: '100%',
    height: CONTAINER_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${theme.spacing.sm} ${theme.spacing.lg} ${theme.spacing.xs}`,
  },
  wrapper: {
    flex: 1,
  },
  input: {
    height: INPUT_HEIGHT,
    minHeight: INPUT_HEIGHT,
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: PILL_RADIUS,
    backgroundColor: theme.white,
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
    // Mantine v6 rem()-wraps both `iconWidth` and `rightSectionWidth` (see Input.js) assuming a 16px root,
    // but this app's root is 10px, so those insets render at 62.5% of their px value. Pin the horizontal
    // insets here in px to bypass that rem shrinkage. `&[data-with-...]` matches the specificity of
    // Mantine's own rules so our px value wins; `!important` beats Mantine's inline padding-right.
    '&[data-with-icon]': {
      paddingLeft: ICON_WIDTH,
    },
    paddingRight: `${showHint ? HINT_WIDTH : PILL_EDGE_PADDING}px !important`,
    fontSize: theme.fontSizes.sm,
    '&::placeholder': {
      fontFamily: theme.fontFamily,
      fontWeight: 400,
      fontStyle: 'italic',
      fontSize: theme.fontSizes.md,
      lineHeight: 1,
      letterSpacing: 0,
    },
  },
  icon: {
    color: theme.colors.gray[6],
    display: 'flex',
    alignItems: 'center',
    width: ICON_WIDTH,
    justifyContent: 'flex-start',
    paddingLeft: PILL_EDGE_PADDING,
  },
  searchIcon: {
    fontSize: ICON_SIZE,
    width: ICON_SIZE,
    height: ICON_SIZE,
    display: 'block',
  },
  rightSection: {
    width: HINT_WIDTH,
    justifyContent: 'flex-end',
    paddingRight: PILL_EDGE_PADDING,
  },
  kbd: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    width: HINT_CHIP_WIDTH,
    height: 20.5,
    borderRadius: 14,
    padding: '2px 6px 2px 7px',
    fontFamily: theme.fontFamily,
    fontWeight: 400,
    fontSize: 12,
    lineHeight: '16.5px',
    letterSpacing: 0,
    color: theme.colors.gray[6],
    whiteSpace: 'nowrap',
    backgroundColor: theme.colors.gray[3],
    // Mantine's Kbd defaults to a border (with a heavier 3px bottom "keycap" lip) plus a white text
    // "highlight" shadow; remove all of the keycap styling.
    border: 'none',
    boxShadow: 'none',
    textShadow: 'none',
  },
}));

interface QuickSearchInputProps {
  query: string;
  onQueryChange: (value: string) => void;
  placeholder: string;
  inputRef: React.RefObject<HTMLInputElement>;
  listboxId?: string;
  activeDescendantId?: string;
  expanded?: boolean;
}

export default function QuickSearchInput({
  query,
  onQueryChange,
  placeholder,
  inputRef,
  listboxId,
  activeDescendantId,
  expanded,
}: QuickSearchInputProps): JSX.Element {
  const showHint = !query;
  const {classes} = useStyles({showHint});
  return (
    <TextInput
      ref={inputRef}
      classNames={{
        root: classes.root,
        wrapper: classes.wrapper,
        input: classes.input,
        icon: classes.icon,
        rightSection: classes.rightSection,
      }}
      value={query}
      onChange={(e) => {
        onQueryChange(e.currentTarget.value);
      }}
      placeholder={placeholder}
      icon={<BenchlingIcon icon="search" className={classes.searchIcon} />}
      iconWidth={ICON_WIDTH}
      // Passing `undefined` when the hint is hidden avoids rendering an empty right-section box.
      rightSection={
        showHint ? (
          <Kbd className={classes.kbd}>
            {shortcutKeys.map((key) => (
              <span key={key}>{key}</span>
            ))}
          </Kbd>
        ) : undefined
      }
      rightSectionWidth={showHint ? HINT_WIDTH : PILL_EDGE_PADDING}
      role="combobox"
      aria-label="Search, navigate, or ask a question"
      aria-autocomplete="list"
      aria-expanded={expanded ?? false}
      aria-controls={listboxId}
      aria-activedescendant={activeDescendantId}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={true}
    />
  );
}
